// TableFilter.tsx
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import type { Column, Table } from "@tanstack/react-table";
import { TextInput, Select } from "flowbite-react";

export function Filter<TData>({
  column,
  table, // not used here
  as = "autocomplete",
  limit = 250,
}: {
  column: Column<TData, unknown>;
  table: Table<TData>;
  as?: "autocomplete" | "select";
  limit?: number;
}) {
  const meta = (column.columnDef.meta as any) || {};
  const isNumber = meta.valueType === "number";

  // Current filter value (string or number depending on column)
  const value = column.getFilterValue() as string | number | undefined;

  // Faceted uniques map: Map<uniqueValue, count>
  const uniqueMap = column.getFacetedUniqueValues?.();

  // Build suggestion list (keep raw values; render as strings)
  const options = React.useMemo(() => {
    if (!uniqueMap) return [];
    const raw = Array.from(uniqueMap.keys()).filter(
      (v) => v !== null && v !== undefined
    );
    // stringify for UI, but don't lose numeric meaning
    const str = raw.map((v) => String(v));
    str.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return str.slice(0, limit);
  }, [uniqueMap, limit]);

  const setFilter = (raw: string) => {
    if (raw === "") {
      column.setFilterValue(undefined);
      return;
    }
    if (isNumber) {
      const n = Number(raw);
      column.setFilterValue(Number.isFinite(n) ? n : undefined);
    } else {
      column.setFilterValue(raw);
    }
  };

  if (as === "select") {
    return (
      <Select
        sizing="sm"
        value={value !== undefined ? String(value) : ""}
        onChange={(e) => setFilter(e.target.value)}
      >
        <option value="">All</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </Select>
    );
  }

  // Default: text input + datalist (native autocomplete)
  const listId = `filter-${column.id}-datalist`;

  return (
    <>
      <TextInput
        type={isNumber ? "number" : "text"}
        inputMode={isNumber ? "decimal" : undefined}
        sizing="sm"
        placeholder={`Filter ${column.columnDef.header}...`}
        value={value !== undefined ? String(value) : ""}
        onChange={(e) => setFilter(e.target.value)}
        list={listId}
      />
      <datalist id={listId}>
        {options.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
    </>
  );
}
